import { inject, observer } from "mobx-react";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardBody, CardHeader, CardText } from "reactstrap";
import Store from "./store";
import { ClientDto } from "./types";
import * as _ from "lodash";
import Button from "../../../components/Button";
import { BiTrash, BiPencil, BiPlus } from "react-icons/bi";
import CreateUpdateModal from "./create-update-modal";
import { MessageResultState } from "../../../types/general-types";

interface IPage {
  ClientManagementStore?: Store;
}

const ClientManagementPage: FC<IPage> = inject("ClientManagementStore")(
  observer((props: IPage) => {
    const { ClientManagementStore: store } = props;
    const [clients, setClients] = useState<ClientDto[]>([]);
    const [modalShow, setModalShow] = useState(false);
    const [selectedRow, setSelectedRow] = useState<ClientDto | undefined>();
    const [expanded, setExpanded] = useState<{ [key: number]: boolean }>({});
    const toggleCreateModal = () => setModalShow(!modalShow);

    const maxDescriptionLength = 150; // Used as a threshold for showing the toggle button

    useEffect(() => {
      store?.getAll().then((res) => {
        if (res.state === MessageResultState.SUCCESS) {
          setClients(res.result);
        }
      });
    }, []);

    const handleExpandToggle = (index: number, event: React.MouseEvent) => {
      event.stopPropagation();
      setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const { i18n, t } = useTranslation();

    const splitNameIntoLines = (name: string) => {
      const words = name.split(" ");
      const lines = [];
      while (words.length > 0) {
        lines.push(words.splice(0, 3).join(" "));
      }
      return lines;
    };

    const CardWrapper = useMemo(() => {
      return (
        <>
          <div className="container" style={{ height: "100%" }}>
            <div className="row">
              <div className="add-new-portal-btn">
                <Button
                  className="btn"
                  style={{
                    float: "right",
                    marginRight: 10,
                    backgroundColor: "#23486A",
                    color: "#faf5f5",
                  }}
                  icon={<BiPlus style={{ marginRight: 3 }} />}
                  text={t("Add New Portal")}
                  onClick={async () => {
                    let maxOrderIndex =
                      Math.max(
                        ...clients.map((x) => {
                          return x.orderIndex;
                        })
                      ) + 1;
                    setSelectedRow({
                      id: "",
                      description: "",
                      description_EN: "",
                      name_EN: "",
                      isLocal: false,
                      logoUrl: "",
                      logoutUri: "",
                      name: "",
                      redirectUri: "",
                      permissionPrefix: "",
                      orderIndex: maxOrderIndex,
                    });
                    toggleCreateModal();
                  }}
                />
              </div>
              {clients.map((client, i) => {
                const isExpanded = expanded[i];
                const clientNameLines = splitNameIntoLines(
                  i18n.language === "en" && client.name_EN
                    ? client.name_EN
                    : client.name
                );
                const description =
                  i18n.language === "en" && client.description_EN
                    ? client.description_EN
                    : client.description;

                return (
                  <div className="col-12 col-md-4 mt-2 py-3" key={i}>
                    <Card
                      className="my-2 m-2 grow"
                      inverse
                      style={{
                        textAlign: "center",
                        background: "linear-gradient(45deg, #A31D1D, #D84040)",
                        height: "100%",
                        cursor: "pointer",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      <CardHeader
                        style={{
                          color: "#fff",
                          fontWeight: "bold",
                        }}
                      >
                        <strong>
                          {clientNameLines.map((line, index) => (
                            <div key={index}>{line}</div>
                          ))}
                        </strong>

                        <Button
                          className="btn edit-button"
                          style={{
                            position: "absolute",
                            top: "5px",
                            left: "5px",
                            border: "none",
                            backgroundColor: "transparent",
                            color: "#faf5f5",
                          }}
                          icon={<BiPencil />}
                          onClick={async () => {
                            setSelectedRow(Object.assign({}, client));
                            toggleCreateModal();
                          }}
                        />

                        <Button
                          className="btn delete-button"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                            border: "none",
                            backgroundColor: "transparent",
                            color: "#faf5f5",
                          }}
                          showConfirm={true}
                          confirmText={t(
                            "Do you want to continue with this action?"
                          )}
                          icon={<BiTrash />}
                          onClick={async () => {
                            await store?.delete(client.id).then((res) => {
                              if (res.state === MessageResultState.SUCCESS) {
                                store!.getAll().then((res) => {
                                  if (
                                    res.state === MessageResultState.SUCCESS
                                  ) {
                                    setClients(res.result);
                                  }
                                });
                              }
                            });
                          }}
                        />
                      </CardHeader>

                      <CardBody
                        style={{
                          color: "#ebd9d9",
                          padding: "1rem",
                        }}
                      >
                        <CardText style={{ textAlign: "left" }}>
                          <label
                            style={{
                              display: "-webkit-box",
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              WebkitLineClamp: !isExpanded ? 3 : "unset",
                            }}
                          >
                            {description}
                          </label>
                          {description.length > maxDescriptionLength && (
                            <button
                              style={{
                                background: "none",
                                border: "none",
                                color: "#e4e4e4",
                                cursor: "pointer",
                              }}
                              onClick={(e) => handleExpandToggle(i, e)}
                            >
                              {isExpanded ? t("Show Less") : t("Read More")}
                            </button>
                          )}
                        </CardText>
                      </CardBody>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
          {modalShow && (
            <CreateUpdateModal
              isShow={modalShow}
              toggleModal={toggleCreateModal}
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
              clients={clients}
              setClients={setClients}
            />
          )}
        </>
      );
    }, [clients, expanded, store, toggleCreateModal]);

    return <>{CardWrapper}</>;
  })
);

export default ClientManagementPage;
